
import React, { useState } from "react";
import { useEditorConfig } from "@/hooks/editor/useEditorConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { EditorContent } from "@tiptap/react";
import { Save, Send, Eye } from "lucide-react";
import "@/components/blog-management/RichTextEditor.css";

interface TemplateEditorProps {
  initialData?: {
    id?: string;
    name: string;
    subject: string;
    previewText?: string;
    content: string;
  };
  onSave: (template: {
    id?: string;
    name: string;
    subject: string;
    previewText?: string;
    content: string;
  }) => void;
  onTest?: (template: {
    id?: string;
    name: string;
    subject: string;
    previewText?: string;
    content: string;
  }) => void;
  onPreview?: (template: {
    id?: string;
    name: string;
    subject: string;
    previewText?: string;
    content: string;
  }) => void;
}

export const TemplateEditor: React.FC<TemplateEditorProps> = ({
  initialData = { name: "", subject: "", previewText: "", content: "" },
  onSave,
  onTest,
  onPreview,
}) => {
  const [template, setTemplate] = useState(initialData);
  const [isSaving, setIsSaving] = useState(false);

  const editor = useEditorConfig({
    content: template.content,
    onChange: (html) => {
      setTemplate((prev) => ({ ...prev, content: html }));
    },
    placeholder: "Write your email template content here...",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setTemplate((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!template.name || !template.subject || !template.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSaving(true);
    try {
      await onSave(template);
      toast.success("Template saved successfully");
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = () => {
    if (onTest) {
      onTest(template);
    }
  };

  const handlePreview = () => {
    if (onPreview) {
      onPreview(template);
    }
  };

  const isFormValid = !!template.name && !!template.subject && !!template.content;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label htmlFor="name">Template Name *</Label>
          <Input
            id="name"
            name="name"
            value={template.name}
            onChange={handleChange}
            placeholder="Monthly Newsletter"
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="subject">Email Subject Line *</Label>
          <Input
            id="subject"
            name="subject"
            value={template.subject}
            onChange={handleChange}
            placeholder="Your Monthly Newsletter from {{company_name}}"
            className="mt-1.5"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="previewText">Preview Text</Label>
        <Textarea
          id="previewText"
          name="previewText"
          value={template.previewText || ""}
          onChange={handleChange}
          placeholder="This text will show as a preview in some email clients"
          className="mt-1.5 resize-none"
        />
        <p className="text-xs text-slate-500 mt-1">
          Preview text appears in email clients after the subject line. Recommended 50-100 characters.
        </p>
      </div>

      <div>
        <Label htmlFor="content">Template Content *</Label>
        <Card className="mt-1.5 border-slate-200">
          <CardContent className="p-0">
            <div className="border-b border-slate-200 p-3 bg-slate-50 flex items-center">
              <div className="text-sm font-medium text-slate-700">Email Content Editor</div>
            </div>
            <div className="p-4">
              <EditorContent editor={editor} className="min-h-[400px]" />
            </div>
          </CardContent>
        </Card>
        <div className="text-xs text-slate-500 mt-1">
          You can use variables like &#123;&#123;subscriber_name&#125;&#125;, &#123;&#123;unsubscribe_link&#125;&#125;
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-3">
        <Button
          variant="outline"
          type="button"
          onClick={handlePreview}
          className="border-slate-200"
        >
          <Eye className="h-4 w-4 mr-2" />
          Preview
        </Button>
        <Button
          variant="outline"
          type="button"
          onClick={handleTest}
          className="border-slate-200"
        >
          <Send className="h-4 w-4 mr-2" />
          Send Test
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isFormValid || isSaving}
          className="bg-slate-900 hover:bg-slate-800"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? "Saving..." : "Save Template"}
        </Button>
      </div>
    </div>
  );
};
